import type { EscalationLevel } from '@badger-sentinel/rules';
import type { TurnPhase } from '@badger-sentinel/protocol';

import { EscalationMeter } from './EscalationMeter.js';
import { SelectionChip } from './SelectionChip.js';
import { DATA_TAG, HEADLINE, LABEL, READOUT, READOUT_OF, TITLE } from '../../theme/type.js';
import type { CountdownView } from '../model/countdown.js';
import type { EscalationProjection } from '../model/escalation.js';
import type { CommitView, OrdersCount } from '../model/orders.js';
import type { Selection } from '../model/selection.js';

/**
 * The status row (design 94–147).
 *
 * Turn, phase, window, order count, escalation posture, the reserved VP slot and
 * the commit control. Everything here is true of the *seat* rather than of the
 * window you happen to be looking at, which is what puts it in chrome (shell.md
 * §Where the line is).
 *
 * This is where the copy rules bite hardest. The one second line in the whole
 * phase is on the commit button, and it earns its place because
 * `LOCKS AT T-00:00` is a real consequence with a value in it rather than a
 * restatement of what the button does.
 */

export const PHASE_LABELS: Readonly<Record<TurnPhase, string>> = Object.freeze({
  INTEL_REPORT: 'INTEL REPORT',
  ORDERS: 'ORDERS',
  RESOLVING: 'RESOLVING',
});

export interface StatusRowProps {
  readonly turn: number | null;
  readonly maxTurns: number | null;
  readonly phase: TurnPhase | null;
  readonly countdown: CountdownView;
  readonly orders: OrdersCount | null;
  readonly escalation: { readonly counter: number; readonly level: EscalationLevel } | null;
  readonly projection: EscalationProjection | null;
  readonly commit: CommitView;
  readonly onCommit: (submitted: boolean) => void;
  /** Phase 2's, and conditional in the design: nothing selected draws nothing. */
  readonly selection: Selection | null;
  readonly onClearSelection: () => void;
}

export function StatusRow(props: StatusRowProps): JSX.Element {
  return (
    <div
      data-mat="chrome"
      data-testid="status-row"
      style={{
        flex: 'none',
        height: 44,
        display: 'flex',
        alignItems: 'stretch',
        padding: '0 12px',
        overflow: 'hidden',
        background: 'var(--surface-2)',
        borderBottom: '1px solid var(--border-strong)',
      }}
    >
      <Cell label="TURN" first>
        <span style={{ ...READOUT, display: 'flex', alignItems: 'baseline' }}>
          <span data-testid="turn">{pad(props.turn)}</span>
          <span style={READOUT_OF}>/{pad(props.maxTurns)}</span>
        </span>
      </Cell>

      <Cell label="PHASE">
        {/*
          Dimmed while the window is not counting, for the same reason the clock
          is: PHASE ORDERS sat beside WINDOW — NOT STARTED at identical weight,
          and two adjacent cells contradicting each other is how a cadet decides
          to start composing during the brief. The phase is true — the turn
          machine is in ORDERS — it is simply not yet live, and this is already
          the console's word for that.
        */}
        <span
          data-testid="phase"
          style={{
            ...TITLE,
            color:
              props.countdown.kind === 'RUNNING' || props.countdown.kind === 'URGENT'
                ? 'var(--text)'
                : 'var(--text-dim)',
          }}
        >
          {props.phase === null ? '—' : PHASE_LABELS[props.phase]}
        </span>
      </Cell>

      <Cell label="WINDOW">
        <span style={{ display: 'flex', alignItems: 'baseline', gap: 7 }}>
          <span data-testid="countdown" style={{ ...READOUT, color: clockColour(props.countdown) }}>
            {props.countdown.clock}
          </span>
          {props.countdown.note !== null && (
            <span
              data-testid="countdown-note"
              style={{ ...LABEL, color: noteColour(props.countdown) }}
            >
              {props.countdown.note}
            </span>
          )}
        </span>
      </Cell>

      <Cell label="ORDERS">
        <span style={{ ...READOUT, display: 'flex', alignItems: 'baseline' }}>
          <span data-testid="orders-count">
            {props.orders === null ? '—' : pad(props.orders.tasked)}
          </span>
          <span style={READOUT_OF}>
            /{props.orders === null ? '—' : pad(props.orders.orderable)}
          </span>
        </span>
      </Cell>

      {props.selection !== null && (
        <SelectionChip selection={props.selection} onClear={props.onClearSelection} />
      )}

      <div style={{ flex: 1, minWidth: 12 }} />

      <div
        style={{
          flex: 'none',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          height: 28,
          alignSelf: 'center',
          padding: '0 12px',
          background: 'var(--surface)',
          border: '1px solid var(--border)',
        }}
      >
        {props.escalation !== null && (
          <EscalationMeter
            counter={props.escalation.counter}
            level={props.escalation.level}
            projection={props.projection}
          />
        )}
        <span style={{ width: 1, height: 14, background: 'var(--rule)' }} />
        {/*
          F1 / M5-D13: §12's evaluator is M6's. The slot is held so the row does
          not relayout when the numbers arrive, and it renders no numbers — a
          faked score is worse than a missing one in a game decided on it.
        */}
        <div data-testid="vp-slot" style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
          <span style={LABEL}>VP</span>
          <span style={{ ...READOUT, fontSize: 16, color: 'var(--text-dim)' }}>—</span>
        </div>
      </div>

      <CommitButton commit={props.commit} onCommit={props.onCommit} />
    </div>
  );
}

/**
 * The commit control.
 *
 * ## The disabled state is a material, not an opacity
 *
 * It used to keep the amber fill at `opacity: .45`, which the design read as
 * "not yet, but this is where it will be". On screen it read as a live amber
 * block: the pre-start and running consoles differed only by the presence of a
 * 10px second line, on the control an operator watches for ninety minutes. A
 * withheld commit now steps *down* to `--surface-2` and keeps its 1px
 * `--primary` border, so the place is still marked and the state is
 * unmistakable.
 *
 * ## `withheld` is rendered, finally
 *
 * `commitFor` has always computed the one reason a seat can act on in ten
 * seconds — `OWES A DECISION`, when Missile Warning still has an open track — and this
 * component put it in `data-withheld` and drew nothing. The e2e suite asserted
 * on the attribute, so the reason was *tested* and never shown. It takes the
 * second line, which the copy rules license for exactly this: a real condition
 * the operator can clear, not a restatement of the button.
 *
 * A withheld reason outranks the consequence — `LOCKS AT T-00:00` is not the
 * useful sentence when the button cannot be pressed at all.
 */
function CommitButton(props: {
  readonly commit: CommitView;
  readonly onCommit: (submitted: boolean) => void;
}): JSX.Element {
  const { commit } = props;
  const secondLine = commit.withheld ?? commit.consequence;
  const held = !commit.submitEnabled;
  const flat = held || commit.submitted;

  return (
    <button
      type="button"
      data-testid="submit-orders"
      data-withheld={commit.withheld ?? undefined}
      disabled={held}
      onClick={() => props.onCommit(commit.next)}
      style={{
        flex: 'none',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
        height: 34,
        alignSelf: 'center',
        marginLeft: 12,
        padding: '0 18px',
        background: flat ? 'var(--surface-2)' : 'var(--primary)',
        border: `1px solid ${commit.submitted ? 'var(--border-strong)' : 'var(--primary)'}`,
        color: flat ? 'var(--text-2)' : 'var(--primary-on)',
        fontFamily: 'var(--font-ui)',
        whiteSpace: 'nowrap',
      }}
    >
      <span style={HEADLINE}>{commit.label}</span>
      {secondLine !== null && (
        <span
          data-testid="commit-second-line"
          style={{
            ...DATA_TAG,
            // A withheld reason is a condition to clear, so it carries the warn
            // colour; a consequence is part of the button's own promise.
            color: commit.withheld !== null ? 'var(--warn)' : undefined,
            opacity: commit.withheld !== null ? 1 : 0.8,
          }}
        >
          {secondLine}
        </span>
      )}
    </button>
  );
}

function Cell(props: { label: string; first?: boolean; children: JSX.Element }): JSX.Element {
  return (
    <div
      style={{
        flex: 'none',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        gap: 1,
        padding: props.first ? '0 16px 0 0' : '0 16px',
        borderLeft: props.first ? undefined : '1px solid var(--rule)',
      }}
    >
      <span style={LABEL}>{props.label}</span>
      {props.children}
    </div>
  );
}

function pad(value: number | null): string {
  return value === null ? '—' : String(value).padStart(2, '0');
}

function clockColour(countdown: CountdownView): string {
  switch (countdown.kind) {
    case 'URGENT':
      return 'var(--red)';
    // A stopped clock has to read stopped: the number stays, dimmed, with the
    // word beside it. At full contrast it is indistinguishable from a countdown
    // that has died, which is the one reading §4.10 exists to prevent.
    case 'PAUSED':
      return 'var(--text-dim)';
    case 'RUNNING':
      return 'var(--text)';
    default:
      return 'var(--text-dim)';
  }
}

function noteColour(countdown: CountdownView): string {
  return countdown.kind === 'PAUSED' ? 'var(--warn)' : 'var(--text-muted)';
}
